import React, { useState, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    IconButton,
    Collapse,
    Autocomplete,
    TextField,
    Button
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Refresh } from '@mui/icons-material';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './Po.module.css'; // Start reusing existing styles where possible
import Header from '../Header';
import { local_signage } from '../Const';
import { useNavigate } from 'react-router-dom';

// Detail Row for nested Sale Details
const DetailRow = ({ detailRow }) => {
    const [detailOpen, setDetailOpen] = useState(false);
    const hasSaleDetails = detailRow.saledtl && detailRow.saledtl.length > 0;

    return (
        <React.Fragment>
            <TableRow>
                <TableCell style={{ paddingLeft: '20px' }}>
                    {hasSaleDetails && (
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setDetailOpen(!detailOpen)}
                        >
                            {detailOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{detailRow.LineNumber}</TableCell>
                <TableCell>{detailRow.ItemID}</TableCell>
                <TableCell>{detailRow.ItemName}</TableCell>
                <TableCell align="right">{detailRow.Qty}</TableCell>
                <TableCell align="right">{detailRow.Rate}</TableCell>
                <TableCell align="right">{detailRow.TaxPercent}</TableCell>
            </TableRow>
            {hasSaleDetails && (
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                        <Collapse in={detailOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1, paddingLeft: 4, bgcolor: '#f8f9fa', borderRadius: 1, padding: 2, border: '1px solid #e9ecef' }}>
                                <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '13px', fontWeight: 'bold', color: '#28a745' }}>
                                    Sale Details
                                </Typography>
                                <Table size="small" aria-label="sales">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Line</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Branch ID</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Sale ID</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Item ID</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }} align="right">Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }} align="right">Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }} align="right">Tax %</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }} align="center">Received</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailRow.saledtl.map((saleItem, idx) => {
                                            const hasGRDetails = saleItem.grdtl && saleItem.grdtl.length > 0;
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ fontSize: '12px' }}>{saleItem.LineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }}>{saleItem.BranchId}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }}>{saleItem.SaleID}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }}>{saleItem.ItemID}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }} align="right">{saleItem.Qty}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }} align="right">{saleItem.Rate}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }} align="right">{saleItem.TaxPercent}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px', fontWeight: 'bold', color: hasGRDetails ? '#28a745' : '#dc3545' }} align="center">
                                                        {hasGRDetails ? 'Yes' : 'No'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
};

// Row Component for Drill-down
const Row = ({ row }) => {
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">{row.OrderID}</TableCell>
                <TableCell>{row.BranchId}</TableCell>
                <TableCell>{row.TransTypeID}</TableCell>
                <TableCell>{dayjs(row.OrderDate).format('DD/MM/YYYY')}</TableCell>
                <TableCell>{row.VendorId}</TableCell>
                <TableCell>{row.SaleOrderID}</TableCell>
                <TableCell>
                    <span className={row.Closed ? styles.indent_status_closed : styles.indent_status_open} style={{ padding: '4px 8px', borderRadius: '4px' }}>
                        {row.Closed ? 'Closed' : 'Open'}
                    </span>
                </TableCell>
            </TableRow>
            <TableRow sx={{ borderBottom: '4px solid #e0e0e0' }}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>
                                Order Details
                            </Typography>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell sx={{ fontWeight: 'bold' }}>Line</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Item ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Tax %</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.sodtl && row.sodtl.map((detailRow, index) => (
                                        <DetailRow key={index} detailRow={detailRow} />
                                    ))}
                                    {(!row.sodtl || row.sodtl.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">No details available</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

export const PoReport = () => {
    const [orderDate, setOrderDate] = useState(dayjs());
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const navigate = useNavigate();

    // Hardcoded branches as requested
    const branches = [
        { label: 'Nagpur (L211)', id: 'L211' },
        { label: 'Raipur (L235)', id: 'L235' }
    ];

    const fetchData = async () => {
        if (!selectedBranch || !orderDate) {
            alert("Please select a Branch and Order Date");
            return;
        }

        setLoading(true);
        try {
            const formattedDate = orderDate.format('YYYYMMDD');
            const url = `${local_signage}/api/v1/reportjsongcorderdetails?BranchID=${selectedBranch.id}&OrderDate=${formattedDate}`;

            console.log("Fetching URL:", url);

            const token = localStorage.getItem("AuthorizationToken");
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const json = await response.json();
                console.log("API Response:", json);
                if (json && json.GCOrder) {
                    setData(json.GCOrder);
                } else {
                    setData([]);
                }
            } else {
                console.error("API Error:", response.status);
                alert("Failed to fetch data");
                setData([]);
            }
        } catch (error) {
            console.error("Fetch error:", error);
            alert("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!data || data.length === 0) {
            alert("No data to export");
            return;
        }

        const exportData = [];

        data.forEach(order => {
            const orderInfo = {
                'Order ID': order.OrderID || 'N/A',
                'Branch ID': order.BranchId || 'N/A',
                'Trans Type': order.TransTypeID || 'N/A',
                'Order Date': order.OrderDate ? dayjs(order.OrderDate).format('DD/MM/YYYY') : 'N/A',
                'Vendor ID': order.VendorId || 'N/A',
                'Sale Order ID': order.SaleOrderID || 'N/A',
                'Status': order.Closed ? 'Closed' : 'Open'
            };

            if (order.sodtl && order.sodtl.length > 0) {
                order.sodtl.forEach(item => {
                    const itemInfo = {
                        ...orderInfo,
                        'Line No': item.LineNumber || 'N/A',
                        'Item ID': item.ItemID || 'N/A',
                        'Item Name': item.ItemName || 'N/A',
                        'SO Qty': item.Qty || 0,
                        'SO Rate': item.Rate || 0,
                        'SO Tax %': item.TaxPercent || 0
                    };

                    // Check for further nested saledtl
                    if (item.saledtl && item.saledtl.length > 0) {
                        item.saledtl.forEach(saleItem => {
                            const saleInfo = {
                                ...itemInfo,
                                'Sale Line No': saleItem.LineNumber || 'N/A',
                                'Sale Branch ID': saleItem.BranchId || 'N/A',
                                'Sale ID': saleItem.SaleID || 'N/A',
                                'Sale Item ID': saleItem.ItemID || 'N/A',
                                'Sale Qty': saleItem.Qty || 0,
                                'Sale Rate': saleItem.Rate || 0,
                                'Sale Tax %': saleItem.TaxPercent || 0
                            };

                            // Check for further nested grdtl (GR Details)
                            if (saleItem.grdtl && saleItem.grdtl.length > 0) {
                                saleItem.grdtl.forEach(grItem => {
                                    exportData.push({
                                        ...saleInfo,
                                        'GR Line No': grItem.LineNumber || 'N/A',
                                        'GR Branch ID': grItem.BranchId || 'N/A',
                                        'GR ID': grItem.GRId || 'N/A',
                                        'GR Item ID': grItem.ItemID || 'N/A',
                                        'GR Qty': grItem.Qty || 0
                                    });
                                });
                            } else {
                                // If no grdtl, push with N/A for GR fields
                                exportData.push({
                                    ...saleInfo,
                                    'GR Line No': 'N/A',
                                    'GR Branch ID': 'N/A',
                                    'GR ID': 'N/A',
                                    'GR Item ID': 'N/A',
                                    'GR Qty': 'N/A'
                                });
                            }
                        });
                    } else {
                        // If no saledtl, push with N/A for Sale and GR fields
                        exportData.push({
                            ...itemInfo,
                            'Sale Line No': 'N/A',
                            'Sale Branch ID': 'N/A',
                            'Sale ID': 'N/A',
                            'Sale Item ID': 'N/A',
                            'Sale Qty': 'N/A',
                            'Sale Rate': 'N/A',
                            'Sale Tax %': 'N/A',
                            'GR Line No': 'N/A',
                            'GR Branch ID': 'N/A',
                            'GR ID': 'N/A',
                            'GR Item ID': 'N/A',
                            'GR Qty': 'N/A'
                        });
                    }
                });
            } else {
                // If no sodtl, push with N/A for all nested fields
                exportData.push({
                    ...orderInfo,
                    'Line No': 'N/A',
                    'Item ID': 'N/A',
                    'Item Name': 'N/A',
                    'SO Qty': 'N/A',
                    'SO Rate': 'N/A',
                    'SO Tax %': 'N/A',
                    'Sale Line No': 'N/A',
                    'Sale Branch ID': 'N/A',
                    'Sale ID': 'N/A',
                    'Sale Item ID': 'N/A',
                    'Sale Qty': 'N/A',
                    'Sale Rate': 'N/A',
                    'Sale Tax %': 'N/A',
                    'GR Line No': 'N/A',
                    'GR Branch ID': 'N/A',
                    'GR ID': 'N/A',
                    'GR Item ID': 'N/A',
                    'GR Qty': 'N/A'
                });
            }
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PO Report");
        XLSX.writeFile(workbook, `PO_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    return (
        <div className={styles.indent_dashboard}>
            <Header />



            <div className={styles.indent_filters_section}>
                <div className={styles.indent_filters_grid} style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'end' }}>

                    <div className={styles.indent_filter_group} style={{ minWidth: '250px' }}>
                        <label>Branch</label>
                        <Autocomplete
                            options={branches}
                            getOptionLabel={(option) => option.label}
                            value={selectedBranch}
                            onChange={(event, newValue) => setSelectedBranch(newValue)}
                            renderInput={(params) => <TextField {...params} placeholder="Select Branch" size="small" />}
                            className={styles.indent_filter_select_segment}
                            sx={{ padding: 0, border: 'none' }}
                        />
                    </div>

                    <div className={styles.indent_filter_group} style={{ minWidth: '200px' }}>
                        <label>Order Date</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={orderDate}
                                onChange={(newValue) => setOrderDate(newValue)}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </div>

                    <div className={styles.indent_filter_actions}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Refresh />}
                            onClick={fetchData}
                            disabled={loading}
                            className={styles.indent_btn_primary}
                            sx={{ height: '40px' }} // Match input height roughly
                        >
                            {loading ? 'Loading...' : 'Get Report'}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleExport}
                            disabled={loading || data.length === 0}
                            className={styles.indent_btn_secondary}
                            sx={{ height: '40px', marginLeft: '10px' }}
                        >
                            Export
                        </Button>
                    </div>
                </div>
            </div>

            <div className={styles.indent_table_section} style={{ padding: '10px' }}>
                <TableContainer component={Paper} className={styles.indent_table_container}>
                    <Table aria-label="collapsible table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell className={styles.indent_sortable}>Order ID</TableCell>
                                <TableCell className={styles.indent_sortable}>Branch</TableCell>
                                <TableCell className={styles.indent_sortable}>Trans Type</TableCell>
                                <TableCell className={styles.indent_sortable}>Order Date</TableCell>
                                <TableCell className={styles.indent_sortable}>Vendor</TableCell>
                                <TableCell className={styles.indent_sortable}>Sale Order ID</TableCell>
                                <TableCell className={styles.indent_sortable}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" style={{ padding: '50px' }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : data.length > 0 ? (
                                data.map((row) => (
                                    <Row key={row.OrderID} row={row} />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" style={{ padding: '50px' }}>
                                        <div className={styles.indent_no_data}>
                                            <p>No data found. Select filters and click Get Report.</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
};